import type { UseQueryResult } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { Card } from "~/components/Card";
import {
  DetailsLayout,
  HistoryCard,
  ManageCard,
  ResourceFields,
} from "~/components/DetailsCards";
import {
  CheckboxField,
  Form,
  FormSkeleton,
  getChangedFields,
  useStudioForm,
} from "~/components/Form";
import { QueryRenderer } from "~/components/QueryRenderer";
import { useDeleteUser, useUpdateUser, useUser } from "~/domain/datastores";
import { pick } from "~/lib/std";
import {
  makeEditUserLocation,
  makeUsersLocation,
  useUserParams,
} from "~/paths";
import type { User } from "~/services/datastore";
import { selectData } from "~/utils";
import { editUserSchema } from "../schemas";

export function UserDetails() {
  const { userId } = useUserParams();

  const query = useUser(userId, { select: selectData });

  const generalSection = <GeneralSection query={query} />;
  const infoSection = <InfoSection query={query} />;
  const historySection = <HistoryCard query={query} />;
  const manageSection = (
    <ManageCard
      resourceName="user"
      query={query}
      editLocation={makeEditUserLocation({ userId })}
      deleteMutation={useDeleteUser(userId)}
      getReadableName={(user) => user.username}
      listLocation={makeUsersLocation()}
    />
  );

  return (
    <DetailsLayout
      primaryGridColumn={generalSection}
      secondaryGridColumn={
        <>
          {infoSection}
          {historySection}
          {manageSection}
        </>
      }
      stack={
        <>
          {generalSection}
          {infoSection}
          {historySection}
          {manageSection}
        </>
      }
    />
  );
}

function GeneralSection({ query }: { query: UseQueryResult<User> }) {
  return (
    <Card title="General">
      <ResourceFields
        query={query}
        fields={[
          { dataType: "id", accessor: "id" },
          { dataType: "text", accessor: "username" },
          { dataType: "role", accessor: "roleId" },
          { dataType: "boolean", accessor: "managed" },
        ]}
      />
    </Card>
  );
}

function InfoSection({ query }: { query: UseQueryResult<User> }) {
  return (
    <Card>
      <QueryRenderer
        query={query}
        loading={<FormSkeleton shapes={["text", "text"]} />}
        success={(user) => <InfoSectionImpl user={user} />}
      />
    </Card>
  );
}

function InfoSectionImpl({ user }: { user: User }) {
  const updateUser = useUpdateUser(user.id);

  const { enqueueSnackbar } = useSnackbar();

  const schema = editUserSchema.pick({ admin: true, disabled: true });
  const FIELDS = schema.keyof().options;

  const {
    control,
    handleSubmit,
    formState: { dirtyFields },
    reset,
  } = useStudioForm({
    schema,
    defaultValues: pick(user, FIELDS),
    onSubmit(values) {
      const changedFields = getChangedFields(values, dirtyFields);

      updateUser.mutate(changedFields, {
        onSuccess(response) {
          enqueueSnackbar("User updated", { variant: "success" });

          reset(pick(response.data, FIELDS));
        },
        onError() {
          enqueueSnackbar("Unable to update user", { variant: "error" });
        },
      });
    },
  });

  return (
    <Form
      onSubmit={handleSubmit}
      loading={updateUser.isLoading}
      submitText="Save Changes"
    >
      <CheckboxField control={control} name="admin" />
      <CheckboxField control={control} name="disabled" />
    </Form>
  );
}
